import * as Actions from '../actions/accountingConnectionActions';
import AccountingConnection from '../../entities/accountingConnection';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type AccountingConnectionAction = { 
    type: string; 
    accountingConnection: AccountingConnection | null; 
    accountingConnectionId: string | AccountingConnection; 
    partial: boolean; 
    lastFetch: number; 
    accountingConnections: AccountingConnection[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<AccountingConnection> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {AccountingConnection[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<AccountingConnection> | null, firmId: string, userId: string, updates?: AccountingConnection[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'accountingConnection', 'accountingConnections', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<AccountingConnection>} data
 * @param {AccountingConnection[]} [updates]
 * @returns {ArrayMap<AccountingConnection>}
 */
function updateStorage(data: ArrayMap<AccountingConnection>, updates?: AccountingConnection[]): ArrayMap<AccountingConnection> {
	if (logging) console.debug('⮂ accountingConnectionReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {AccountingConnection[]} [updates]
 * @returns {ArrayMap<AccountingConnection>}
 */
function updateStorageNoReturn(updates?: AccountingConnection[]): void {
	if (logging) console.debug('⮂ accountingConnectionReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {AccountingConnection[]} [data]
 * @returns {ArrayMap<AccountingConnection>}
 */
function createState(data?: AccountingConnection[] | ArrayMap<AccountingConnection>): ArrayMap<AccountingConnection> {
	if (!data) return ArrayMapRegistry.clone<AccountingConnection>('accountingConnection', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<AccountingConnection>('accountingConnection', data.map(e => e));
    else return ArrayMapRegistry.clone<AccountingConnection>('accountingConnection', data);
}

function accountingConnections(state: ArrayMap<AccountingConnection>, action: AccountingConnectionAction): ArrayMap<AccountingConnection> {
	if (!state) state = createState();

	let st: ArrayMap<AccountingConnection>;

	switch (action.type) {
		// action.accountingConnection - the new value of accountingConnection with id action.id
		// action.accountingConnectionId - id of the target, used if new value is null
		case Actions.ACCOUNTINGCONNECTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ accountingConnectionReducer: FETCH_ONE_SUCCESS', action);

			if (action.accountingConnection === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.accountingConnectionId)) {
				st = state.clone();
				st.set(action.accountingConnection);
				// state.clear();

				updateStorageNoReturn([action.accountingConnection]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.accountingConnections
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.accountingConnections
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.accountingConnections
			 !partial, !stateEmpty, fetchEmpty	-> D return accountingConnections in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge accountingConnections in state newer than those in action.accountingConnections
		 */
		case Actions.ACCOUNTINGCONNECTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.accountingConnections.length === 0;
			const fetched = action.accountingConnections.map(accountingConnection => new AccountingConnection({...accountingConnection, persisted: true}));

			if (logging) console.debug(`⮂ accountingConnectionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} accountingConnections with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ accountingConnectionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ accountingConnectionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<AccountingConnection> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: AccountingConnection[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(accountingConnection => {
					const current = st.get(accountingConnection.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && accountingConnection.status === 'D') {
							// Remove it from state
							toUpdate.push(accountingConnection);
							st.delete(accountingConnection);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (accountingConnection.addedOn > current.addedOn) {
								toUpdate.push(accountingConnection);
								st.set(accountingConnection);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (accountingConnection.status !== 'D') {
							toUpdate.push(accountingConnection);
							st.set(accountingConnection);
						}
						else if (logging) console.debug('⮂ accountingConnectionReducer: partialMerge: skipping deleted accountingConnection not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ accountingConnectionReducer: empty: accountingConnections', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ accountingConnectionReducer: fullMerge: accountingConnections', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localAccountingConnection => {
                    // If this accountingConnection has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localAccountingConnection.persisted) {
                        const fetchedAccountingConnection = st.get(localAccountingConnection.id);
                        if (!fetchedAccountingConnection || localAccountingConnection.addedOn > fetchedAccountingConnection.addedOn) st.set(localAccountingConnection);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE:
			console.error('ACCOUNTINGCONNECTION_FETCH_ONE_FAILURE', action.accountingConnectionId, action.err);

			// No update to storage required
			return state;

		case Actions.ACCOUNTINGCONNECTION_FETCH_FAILURE:
			console.error('ACCOUNTINGCONNECTION_FETCH_FAILURE', action.accountingConnectionId, action.err);

			// No update to storage required
			return state;

		case Actions.ACCOUNTINGCONNECTION_RESTORE: {
		    let accountingConnection = new AccountingConnection({...action.accountingConnection});
            st = state.clone();

            const existing = st.get(accountingConnection.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (accountingConnection.addedOn >= existing.addedOn) {
                    st.set(accountingConnection);
                    return updateStorage(st, [accountingConnection]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (accountingConnection.status === 'D') return state;

                st.set(accountingConnection);
                return updateStorage(st, [accountingConnection]);
            }
        }
            
		case Actions.ACCOUNTINGCONNECTION_SAVE: {
		    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE reducer', action.accountingConnection?.id);
		    
			// clone the accountingConnection
			let accountingConnection = new AccountingConnection({...action.accountingConnection});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new accountingConnection in the state
			// If this is a create, add to the queue
			const _accountingConnection = st.get(accountingConnection.id);

			if (!_accountingConnection) st.set(accountingConnection);
			else {
				if (accountingConnection.addedOn >= _accountingConnection.addedOn) st.set(accountingConnection);
			}

			return updateStorage(st, [accountingConnection]);
		}

		case Actions.ACCOUNTINGCONNECTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY reducer', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));
		    
			// clone the accountingConnections
			let toProcess = action.accountingConnections.map(accountingConnection => new AccountingConnection(accountingConnection));

			st = state.clone();
			// state.clear();

			const updates: AccountingConnection[] = [];

			toProcess.forEach(accountingConnection => {
				// Add or update the new accountingConnection in the state
				// If this is a create, add to the queue
				const existing = st.get(accountingConnection.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (accountingConnection.status !== 'D') {
						st.push(accountingConnection);
						updates.push(accountingConnection);
					}
					else if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY: skipping deleted accountingConnection not in state');
				}
				else {
					if (accountingConnection.addedOn >= existing.addedOn) {
						st.set(accountingConnection);
						updates.push(accountingConnection);
					}
					else if (logging) console.debug('⮂ Skipping, existing accountingConnection had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACCOUNTINGCONNECTION_REMOVE: {
			// clone the accountingConnection
			const accountingConnection = new AccountingConnection({...action.accountingConnection});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(accountingConnection);

			return updateStorage(st, [accountingConnection]);
		}

		case Actions.ACCOUNTINGCONNECTION_RESTORE_FAILURE:
		case Actions.ACCOUNTINGCONNECTION_SAVE_FAILURE: {
		    console.warn('ACCOUNTINGCONNECTION_SAVE_FAILURE reducer', action.accountingConnection?.id);
		    if (!action.accountingConnection?.id) {
				console.warn('ACCOUNTINGCONNECTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const accountingConnection = ensure(AccountingConnection, action.accountingConnection);
			if (!accountingConnection) {
				console.error('Could not find accountingConnection after call to save');
				return state;
			}

			st = state.clone();

			const _accountingConnectionRef = state.get(accountingConnection.id);

			if (_accountingConnectionRef && (_accountingConnectionRef.addedOn === accountingConnection.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _accountingConnection = new AccountingConnection({..._accountingConnectionRef});
				_accountingConnection.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_accountingConnection.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('accountingConnection', _accountingConnection).then(r => {
						if (r.isErr()) console.error(`⮂ accountingConnectionReducer: failed to save accountingConnection with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ accountingConnectionReducer: saved accountingConnection with fatalError info');
					}).catch(err => console.error(`⮂ accountingConnectionReducer: failed to save accountingConnection with fatalError info: ${err.message}`));

				st.set(_accountingConnection);
			}

			return updateStorage(st);
		}

		case Actions.ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE: {
		    console.warn('ACCOUNTINGCONNECTION_SAVE_MANY_FAILURE reducer', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ACCOUNTINGCONNECTION_RESTORE_SUCCESS:
		case Actions.ACCOUNTINGCONNECTION_SAVE_SUCCESS: {
			if (!action.accountingConnection?.id) {
				console.warn('ACCOUNTINGCONNECTION_SAVE_SUCCESS reducer');
                return state;
			}

			const accountingConnectionRef = state.get(action.accountingConnection?.id);
			if (!accountingConnectionRef) {
				console.warn('Could not find accountingConnection after call to save');
                return state;
			}

			st = state.clone();

			if (action.accountingConnection.addedOn >= accountingConnectionRef.addedOn) {
				st.set(action.accountingConnection);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const accountingConnection = new AccountingConnection({...accountingConnectionRef, persisted: true});
				st.set(accountingConnection);
			}

			return updateStorage(st, [action.accountingConnection]);
		}

		case Actions.ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ACCOUNTINGCONNECTION_SAVE_MANY_SUCCESS reducer', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const accountingConnections: AccountingConnection[] = [];

			action.accountingConnections.forEach(e => {
				const accountingConnectionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!accountingConnectionRef) console.warn('Could not find accountingConnection after call to save');
				else if (e.addedOn >= accountingConnectionRef.addedOn) {
				    st.set(e);
				    accountingConnections.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const accountingConnection = new AccountingConnection({...accountingConnectionRef, persisted: true});
					st.set(accountingConnection);
					accountingConnections.push(accountingConnection);
				}
			});

			return updateStorage(st, accountingConnections);
		}

		case Actions.ACCOUNTINGCONNECTION_REMOVE_FAILURE: {
		    console.warn('ACCOUNTINGCONNECTION_REMOVE_FAILURE action', action.accountingConnection?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ACCOUNTINGCONNECTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ACCOUNTINGCONNECTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_RESTORE_MANY reducer', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));

			const toProcess = action.accountingConnections.map(accountingConnection => new AccountingConnection({...accountingConnection}));
			st = state.clone();

			const updates: AccountingConnection[] = [];

			toProcess.forEach(accountingConnection => {
				const existing = st.get(accountingConnection.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (accountingConnection.addedOn >= existing.addedOn) {
						st.set(accountingConnection);
						updates.push(accountingConnection);
					}
					else if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_RESTORE_MANY: skipping older version of accountingConnection');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (accountingConnection.status !== 'D') {
						st.set(accountingConnection);
						updates.push(accountingConnection);
					}
					else if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_RESTORE_MANY: skipping deleted accountingConnection not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACCOUNTINGCONNECTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ ACCOUNTINGCONNECTION_REMOVE_MANY reducer', action.accountingConnections.map(accountingConnection => accountingConnection.id).join(','));

			const toProcess = action.accountingConnections.map(accountingConnection => new AccountingConnection({...accountingConnection}));
			st = state.clone();

			toProcess.forEach(accountingConnection => {
				st.delete(accountingConnection);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default accountingConnections;